/**
 * Report pages — Trial Balance, P&L, Balance Sheet, Cash Flow.
 * Compact, all in one file. Each is a small self-contained component.
 */
import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { reportTB, reportPL, reportBS, reportCF, getBooksTenant } from './api';

const fmt = (n, cur) => {
  const sym = { GBP: '£', INR: '₹', MYR: 'RM ', KZT: '₸', USD: '$', EUR: '€' }[cur] || '';
  const v = Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${sym}${v}`;
};

const Loading = () => <div className="p-12 text-center text-[#0b1a2e]/50"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading report…</div>;

const H = ({ eyebrow, title }) => (
  <div className="mb-6">
    <div className="text-xs font-semibold tracking-[0.3em] text-[#0f5c3d] uppercase">{eyebrow}</div>
    <h1 className="text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>{title}</h1>
  </div>
);

const Card = ({ children }) => <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">{children}</div>;

// ---------- Trial Balance ----------
export const TrialBalancePage = () => {
  const [d, setD] = useState(null);
  const cur = getBooksTenant()?.base_currency;
  useEffect(() => { reportTB().then(setD).catch(() => setD(null)); }, []);
  if (!d) return <><H eyebrow="Report" title="Trial Balance" /><Loading /></>;
  return (
    <div>
      <H eyebrow="Report" title="Trial Balance" />
      <div className="text-xs text-[#0b1a2e]/60 mb-3">As of {d.as_of}</div>
      <Card>
        <table className="w-full text-sm" data-testid="tb-table">
          <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">
            <tr><th className="text-left px-4 py-2">Code</th><th className="text-left px-4 py-2">Account</th><th className="text-right px-4 py-2 w-32">Debit</th><th className="text-right px-4 py-2 w-32">Credit</th></tr>
          </thead>
          <tbody>
            {d.rows.filter((r) => r.debit || r.credit).map((r) => (
              <tr key={r.account_id} className="border-t border-slate-100">
                <td className="px-4 py-1.5 font-mono text-xs">{r.code}</td>
                <td className="px-4 py-1.5">{r.name}</td>
                <td className="px-4 py-1.5 text-right font-mono">{r.debit ? fmt(r.debit, cur) : ''}</td>
                <td className="px-4 py-1.5 text-right font-mono">{r.credit ? fmt(r.credit, cur) : ''}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-[#0f5c3d] font-semibold">
              <td colSpan="2" className="px-4 py-2 text-right text-[#0b1a2e]">Totals</td>
              <td className="px-4 py-2 text-right font-mono">{fmt(d.total_debit, cur)}</td>
              <td className="px-4 py-2 text-right font-mono">{fmt(d.total_credit, cur)}</td>
            </tr>
            <tr><td colSpan="4" className={`px-4 py-2 text-right text-xs ${d.balanced ? 'text-[#0f5c3d]' : 'text-red-600'}`}>{d.balanced ? '✓ Balanced' : '⚠ Not balanced'}</td></tr>
          </tfoot>
        </table>
      </Card>
    </div>
  );
};

// ---------- P&L ----------
export const ProfitLossPage = () => {
  const [d, setD] = useState(null);
  const cur = getBooksTenant()?.base_currency;
  useEffect(() => { reportPL().then(setD).catch(() => setD(null)); }, []);
  if (!d) return <><H eyebrow="Report" title="Profit & Loss" /><Loading /></>;
  return (
    <div>
      <H eyebrow="Report" title="Profit & Loss" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ReportSection title="Income" rows={d.income} total={d.total_income} cur={cur} color="text-[#0f5c3d]" testid="pl-income" />
        <ReportSection title="Expenses" rows={d.expense} total={d.total_expense} cur={cur} color="text-red-600" testid="pl-expense" />
      </div>
      <Card><div className={`px-4 py-3 text-right border-t-4 ${d.net_profit >= 0 ? 'border-[#0f5c3d]' : 'border-red-600'} mt-4`}>
        <span className="text-xs uppercase tracking-widest text-[#0b1a2e]/60 mr-3">Net Profit</span>
        <span className={`text-2xl font-medium ${d.net_profit >= 0 ? 'text-[#0f5c3d]' : 'text-red-600'}`} style={{ fontFamily: 'Cormorant Garamond, serif' }}>{fmt(d.net_profit, cur)}</span>
      </div></Card>
    </div>
  );
};

// ---------- Balance Sheet ----------
export const BalanceSheetPage = () => {
  const [d, setD] = useState(null);
  const cur = getBooksTenant()?.base_currency;
  useEffect(() => { reportBS().then(setD).catch(() => setD(null)); }, []);
  if (!d) return <><H eyebrow="Report" title="Balance Sheet" /><Loading /></>;
  return (
    <div>
      <H eyebrow="Report" title="Balance Sheet" />
      <div className="text-xs text-[#0b1a2e]/60 mb-3">As of {d.as_of}</div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ReportSection title="Assets" rows={d.assets} total={d.total_assets} cur={cur} color="text-[#0f5c3d]" testid="bs-assets" />
        <div className="space-y-4">
          <ReportSection title="Liabilities" rows={d.liabilities} total={d.total_liabilities} cur={cur} color="text-orange-600" testid="bs-liab" />
          <ReportSection title="Equity" rows={[...d.equity, { code: '3999', name: 'Current Year Earnings', balance: d.current_year_earnings }]} total={d.total_equity_with_earnings} cur={cur} color="text-blue-600" testid="bs-equity" />
        </div>
      </div>
      <Card><div className={`px-4 py-3 flex justify-between border-t-4 ${d.balanced ? 'border-[#0f5c3d]' : 'border-red-600'} mt-4`}>
        <div>
          <span className="text-xs uppercase tracking-widest text-[#0b1a2e]/60 mr-3">Total Assets</span>
          <span className="text-lg font-medium text-[#0f5c3d]">{fmt(d.total_assets, cur)}</span>
        </div>
        <div>
          <span className="text-xs uppercase tracking-widest text-[#0b1a2e]/60 mr-3">Liabilities + Equity</span>
          <span className="text-lg font-medium text-[#0f5c3d]">{fmt(d.total_liabilities_plus_equity, cur)}</span>
        </div>
        <span className={`text-xs ${d.balanced ? 'text-[#0f5c3d]' : 'text-red-600'}`}>{d.balanced ? '✓ Balanced' : '⚠ Not balanced'}</span>
      </div></Card>
    </div>
  );
};

// ---------- Cash Flow ----------
export const CashFlowPage = () => {
  const [d, setD] = useState(null);
  const cur = getBooksTenant()?.base_currency;
  useEffect(() => { reportCF().then(setD).catch(() => setD(null)); }, []);
  if (!d) return <><H eyebrow="Report" title="Cash Flow" /><Loading /></>;
  return (
    <div>
      <H eyebrow="Report" title="Cash Flow" />
      <div className="text-xs text-[#0b1a2e]/60 mb-3">Direct method — bank + cash movements</div>
      <Card>
        <table className="w-full text-sm" data-testid="cf-table">
          <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">
            <tr><th className="text-left px-4 py-2">Date</th><th className="text-left px-4 py-2">Journal</th><th className="text-left px-4 py-2">Narration</th><th className="text-right px-4 py-2 w-32">Movement</th></tr>
          </thead>
          <tbody>
            {d.movements.map((m, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="px-4 py-1.5 text-xs text-[#0b1a2e]/70">{m.date}</td>
                <td className="px-4 py-1.5 font-mono text-xs">{m.journal_no}</td>
                <td className="px-4 py-1.5">{m.narration || m.reference}</td>
                <td className={`px-4 py-1.5 text-right font-mono ${m.amount >= 0 ? 'text-[#0f5c3d]' : 'text-red-600'}`}>{fmt(m.amount, cur)}</td>
              </tr>
            ))}
            {d.movements.length === 0 && <tr><td colSpan="4" className="px-4 py-8 text-center text-[#0b1a2e]/40 italic">No cash movements yet.</td></tr>}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-[#0f5c3d] font-semibold">
              <td colSpan="3" className="px-4 py-2 text-right text-[#0b1a2e]">Net change</td>
              <td className={`px-4 py-2 text-right font-mono ${d.net_change >= 0 ? 'text-[#0f5c3d]' : 'text-red-600'}`}>{fmt(d.net_change, cur)}</td>
            </tr>
          </tfoot>
        </table>
      </Card>
    </div>
  );
};

const ReportSection = ({ title, rows, total, cur, color, testid }) => (
  <Card>
    <div className="px-4 py-2 bg-[#f7fbf8] border-b border-black/5 text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]">{title}</div>
    <table className="w-full text-sm" data-testid={testid}>
      <tbody>
        {rows.filter((r) => (r.balance ?? 0) !== 0).map((r, i) => (
          <tr key={i} className="border-t border-slate-100 first:border-t-0">
            <td className="px-4 py-1.5 font-mono text-xs w-20">{r.code}</td>
            <td className="px-4 py-1.5">{r.name}</td>
            <td className="px-4 py-1.5 text-right font-mono">{fmt(r.balance, cur)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr className="border-t border-[#0f5c3d]/30">
          <td colSpan="2" className="px-4 py-2 text-right text-xs uppercase tracking-widest text-[#0b1a2e]/70">Total {title}</td>
          <td className={`px-4 py-2 text-right font-mono font-semibold ${color}`}>{fmt(total, cur)}</td>
        </tr>
      </tfoot>
    </table>
  </Card>
);
